import { Injectable, TemplateRef } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UserLevel } from '@app/core/models/user-level';
import { GenericBreadcrumb } from '@app/menu/models/generic-breadcrumb';
import { MenuData } from '@app/menu/models/menu-data';
import { UserLevelMapping } from '@app/menu/utils/user-level-mapping';
import { BackendSystemService } from '@app/portal/services/backend-system.service';
import { findRouteName } from '@app/routing/business-routes';
import { CurrentRouteData } from '@app/routing/models/current-route-data';
import { RouteDataChange } from '@app/routing/models/route-data-change';
import { RouterService } from '@app/routing/router.service';
import { AutomaticallyDisposedSubscriptions } from '@app/utils/automatically-disposed-subscriptions';
import { loggerFor } from '@app/utils/logger';
import { notNullish } from '@app/utils/rxjs';
import { toggleClass } from '@app/utils/toggle-class';
import { Observable, ReplaySubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  /**
   * Private subject and public observable (this ensures that only the menuService can emit new values)
   */
  private _genericBreadcrumb$: Subject<GenericBreadcrumb | 'will-change' | undefined> = new ReplaySubject(1);

  // The inspection is ignored because for some reason Webstorm doesn't see the usage in the method "isActive"
  // noinspection JSMismatchedCollectionQueryUpdate
  private activeLevels: UserLevel[];
  private logger = loggerFor('MenuService');

  private menuData: MenuData | null = null;

  private _menuData$ = new ReplaySubject(1);

  get menuData$(): Observable<MenuData> {
    return this._menuData$.asObservable().pipe(notNullish()) as Observable<MenuData>;
  }

  /**
   * Private subject and public observable (this ensures that only the menuService can emit new values)
   */
  public get genericBreadcrumb$(): Observable<GenericBreadcrumb | 'will-change' | undefined> {
    return this._genericBreadcrumb$;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private backendSystemService: BackendSystemService,
    private routerService: RouterService
  ) {}

  /**
   * Reset the generic breadcrumb, so it displays the translation of the current route again
   */
  public clearGenericBreadcrumb() {
    this._genericBreadcrumb$.next(undefined);
  }

  /**
   * Initialize the menu service
   */
  public initialize(): any {
    this.routerService.routeDataChange$.subscribe({
      next: (change) => {
        if (this.routeUrlPathDiffers(change) === true) {
          this.updateMenuState(change.current);

          // Reset the generic breadcrumb upon route change
          this.clearGenericBreadcrumb();

          // Dispose subscriptions
          AutomaticallyDisposedSubscriptions.dispose();
        }
      },
    });

    this.logger.debug('MenuService up and running');
  }

  /**
   * Check if URL path, until matrix params, is the same between old and current route data.
   * @param change
   */
  private routeUrlPathDiffers(change: RouteDataChange): boolean {
    if (change.old?.url === change.current.url) {
      return false;
    }
    const oldPath = change.old?.url.split(';')[0] ?? '';
    const newPath = change.current.url.split(';')[0] ?? '';
    return oldPath !== newPath;
  }

  /**
   * Check if the given userLevel is currently active, e.g. if the user is on the service provider
   * level then the levels Enterprise and EndUser are not active.
   * @param userLevel
   * @param exclusiveCheck Checks if exactly the defined userLevel is active
   */
  public isActive(userLevel: UserLevel, exclusiveCheck: boolean = false) {
    let returnValue: boolean;

    if (!exclusiveCheck) {
      returnValue = (this.activeLevels || []).indexOf(userLevel) > -1;
    } else {
      returnValue = (this.activeLevels || [])[0] === userLevel;
    }

    return returnValue;
  }

  switch(parameterName: string, params: Record<string, number | string>): void {
    const routeChange$ = this.routerService.refreshRoute(params, parameterName);

    // in case a backend system will be changed due to the route change
    // we need to inform the backend system service to wait with window reload until the route has changed
    this.backendSystemService.locationChangeInProgressP = routeChange$;
  }

  /**
   * Replace the generic breadcrumb (that only translates the route name) with the given template
   *
   * @param template
   * @param context
   */
  public updateGenericBreadcrumb(template: TemplateRef<any>, context?: any) {
    this._genericBreadcrumb$.next({ template, context });
  }

  /**
   * Let the breadcrumb component know that there is a new generic breadcrumb to come. It will
   * display the current breadcrumb until a new one is passed.
   */
  public breadcrumbWillChange(): void {
    this._genericBreadcrumb$.next('will-change');
  }

  /**
   * Emit new menu data and update css classes of document.body
   */
  private handleMenuDataChange(menuData: MenuData | null) {
    if (menuData != null) {
      const { isEndUser, isEnterprise, isServiceProvider } = menuData.activeUserLevels;
      toggleClass('menu-level--end-user', document.body, isEndUser);
      toggleClass('menu-level--enterprise', document.body, isEnterprise);
      toggleClass('menu-level--service-provider', document.body, isServiceProvider);
    }

    this.logger.debug('Update menuData', menuData);
    this._menuData$.next(menuData);
  }

  private refreshActiveUserLevels(previousMenuData: MenuData) {
    const isEnterprise = this.authenticationService.userIsEnterpriseAdmin();
    const isServiceProvider = this.authenticationService.userIsServiceProviderAdmin();

    const activeLevels = {
      isEndUser: this.isActive(UserLevel.EndUser),
      isEnterprise: (isEnterprise || isServiceProvider) && this.isActive(UserLevel.EnterpriseAdmin),
      isServiceProvider: isServiceProvider && this.isActive(UserLevel.ServiceProviderAdmin),
      isSystemAdmin: this.isActive(UserLevel.SystemAdmin) && this.authenticationService.userIsSystemAdmin(),
    };

    let menuLevelChanged: boolean;

    if (previousMenuData == null) {
      menuLevelChanged = true;
    } else {
      menuLevelChanged =
        activeLevels.isEndUser !== previousMenuData.activeUserLevels.isEndUser ||
        activeLevels.isEnterprise !== previousMenuData.activeUserLevels.isEnterprise ||
        activeLevels.isServiceProvider !== previousMenuData.activeUserLevels.isServiceProvider;
    }

    return {
      ...activeLevels,
      menuLevelChanged,
    };
  }

  /**
   * If force is true, shows the sidebar. If force is false, hides the sidebar.
   * @param force If force is not given, "toggles" sidebar, hiding it if it's present and showing it if it's not present.
   * @returns true if sidebar is now present, and false otherwise.
   */
  public toggleSidebar(force?: boolean): boolean {
    return document.body.classList.toggle('nav-show', force);
  }

  private updateMenuState(state: CurrentRouteData) {
    if (this.authenticationService.isAuthenticated) {
      this.activeLevels = UserLevelMapping.filter((userLevel) => userLevel.isActive(state.url)).map(
        (mapping) => mapping.userLevel
      );

      const mapping = UserLevelMapping.find((level) => level.isActive(state.url));
      const routeName = state.data.name ?? findRouteName(state.url);

      if (mapping !== undefined) {
        this.menuData = {
          activeUserLevels: this.refreshActiveUserLevels(this.menuData),
          enterpriseId: state.context.enterpriseId,
          routeName,
          routeParams: state.params,
          serviceProviderId: state.context.serviceProviderId,
          userId: state.context.userId,
          userLevel: mapping.userLevel,
        };

        this.handleMenuDataChange(this.menuData);
      }

      this.toggleSidebar(false);
    } else {
      this.menuData = null;

      this.handleMenuDataChange(this.menuData);
    }
  }
}
